import { useTranslation } from 'react-i18next'
import { AppForm, AppFormProps } from 'src/components/FormCore'
import { IProjectInfo } from 'src/generated-types/project'
import { useZodForm } from 'src/hooks/zod-form'
import { ShowDialogProps } from 'src/show-dialog'
import { z } from 'zod'
import SetupFacilityForm from './SetupFacilityForm'
import { Box, darken, useTheme } from '@mui/material'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { MainButton } from 'src/components/MainButton'
import { invalidateProjectCache, useUpdateProject, useUpdateProjectGridLines } from 'src/api/project'
import { invalidateProjectPositionCache } from 'src/api/projectPosition'
import { toast } from 'react-toastify'
import { IMooringType, IPositionWithDrawing } from 'src/generated-types'
import { useNavigate } from 'react-router-dom'
import { AppRoutesNavigation } from 'src/app-routes'
import FormStepper from './components/component/FormStepper'
import { useState } from 'react'
import { getThemeColor } from 'src/utils'
import { toNumberOrNull } from 'src/utils/function'
import { MUIThemeColors } from 'src/mui-theme'
import { useDialogCtx } from 'src/hooks/context-hooks'

interface SetupFacilityDialogProps extends ShowDialogProps, AppFormProps {
  project: IProjectInfo
  customerId: EntityId
  withSteps?: boolean
  steps?: [{ label: string; completed: boolean; active: boolean }]
  isConfigurePositions?: boolean
  onStepBack?: (
    positions?: IPositionWithDrawing[],
    type?: IMooringType,
    noOfCages?: number,
    noOfPens?: number,
    noOfRows?: number,
    gridLineWidthInMeters?: number | null,
    gridLineHeightInMeters?: number | null,
  ) => void
}

const SetupFacilityDialog: React.FC<SetupFacilityDialogProps> = ({
  project,
  pop,
  customerId,
  withSteps = false,
  steps,
  isConfigurePositions = true,
  onStepBack,
}) => {
  const { t: translate } = useTranslation('projects')
  const theme = useTheme()
  const { mutateAsync: updateProject } = useUpdateProject(customerId as EntityId, project.localityId)
  const { mutateAsync: updateGridLines } = useUpdateProjectGridLines(
    customerId as EntityId,
    project.localityId,
    project.id as EntityId,
  )
  const navigate = useNavigate()
  const [hasPendingChanges, setHasPendingChanges] = useState<boolean>(false)
  const { showWarningDialog } = useDialogCtx()

  const positiveOptionalNumber = z
    .union([z.string(), z.number()])
    .optional()
    .nullable()
    .transform(val => (val === '' || val === undefined || val === null ? null : Number(val)))
    .refine(val => val === null || (!Number.isNaN(val) && val > 0), translate('mustBePositive', { ns: 'common' }))

  const projectSchema = z
    .object({
      id: z.number(),
      name: z.string().min(1, translate('project-name-required')),
      description: z.string().optional().nullable(),
      ourContactId: z.string().min(1),
      ourContact: z
        .object({
          id: z.string(),
        })
        .optional()
        .nullable()
        .transform(value => (value ? value.id : undefined)),
      customerContactId: z.string().optional().nullable(),
      customerContact: z
        .object({
          id: z.string(),
        })
        .optional()
        .nullable()
        .transform(value => (value ? value.id : undefined)),
      customerId: z.number(),
      fileIds: z.union([z.array(z.string()), z.array(z.number())]).optional(),
      type: z.number().optional().nullable(),
      noOfCages: z
        .union([z.string(), z.number()])
        .optional()
        .nullable()
        .transform(val => (val === '' || val === undefined || val === null ? undefined : Number(val)))
        .transform(val => (val === undefined || Number.isNaN(val) ? undefined : val)),

      noOfPens: z
        .union([z.string(), z.number()])
        .optional()
        .nullable()
        .transform(val => (val === '' || val === undefined || val === null ? undefined : Number(val)))
        .refine(val => val === undefined || val >= 0, translate('mustBeZeroOrPositive', { ns: 'common' })),

      noOfRows: z
        .union([z.string(), z.number()])
        .optional()
        .nullable()
        .transform(val => (val === '' || val === undefined || val === null ? undefined : Number(val)))
        .transform(val => (val === undefined || Number.isNaN(val) ? undefined : val)),
      positions: z.array(z.any()).optional(),
      serviceRow: z.boolean().optional().nullable(),
      localityId: z.number(),
      gridLineWidthInMeters: positiveOptionalNumber,
      gridLineHeightInMeters: positiveOptionalNumber,
    })
    .superRefine((values, ctx) => {
      // Allow submit when type is not selected.
      if (values.type == null) {
        return
      }

      if (values.noOfCages !== undefined && values.noOfCages <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['noOfCages'],
          message: translate('mustBePositive', { ns: 'common' }),
        })
      }

      if (values.type !== IMooringType.LadderCage && values.noOfRows !== undefined && values.noOfRows <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['noOfRows'],
          message: translate('mustBePositive', { ns: 'common' }),
        })
      }

      if (values.type === IMooringType.FrameCage) {
        if (!values.noOfPens || values.noOfPens <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['noOfPens'],
            message: translate('mustBePositive', { ns: 'common' }),
          })
        } else if (values.noOfCages !== undefined && values.noOfPens > values.noOfCages) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['noOfPens'],
            message: translate('pens-cannot-exceed-cages'),
          })
        }
      }
    })
  const form = useZodForm(projectSchema, { defaultValues: { ...project } })

  const onSubmit = form => {
    updateProject(form, {
      onSuccess: () => {
        invalidateProjectCache.getProjects(form.customerId)
        invalidateProjectCache.getProject(form.customerId, project.localityId, project.id as EntityId)
        invalidateProjectPositionCache.getProjectPositions(form.customerId, project.id as EntityId)
        toast.success(translate('updatedSuccessfully', { ns: 'common' }))
        navigate(
          AppRoutesNavigation.Project.project(
            project.customerId as EntityId,
            project.localityId as EntityId,
            project.id as EntityId,
          ),
        )
        pop()
      },
      onError: () => {
        toast.error(translate('unExpectedError', { ns: 'common-api-message' }))
      },
    })
  }

  const handleSubmit = async () => {
    const type = form.getValues('type')
    const isBargeOrLadder = type === IMooringType.Barge || type === IMooringType.LadderCage

    if (isBargeOrLadder) {
      await form.handleSubmit(onSubmit)()
      return
    }

    const rows = form.getValues('noOfRows')
    const cages = form.getValues('noOfCages')

    if (!rows || rows <= 0 || !cages) {
      await form.handleSubmit(onSubmit)()
      return
    }

    if (cages % rows !== 0) {
      toast.error(translate('cages-must-be-multiple-of-rows', { ns: 'projects' }))
      return
    }

    await form.handleSubmit(onSubmit)()
  }

  const handleComplete = async () => {
    const isValid = await form.trigger(['gridLineWidthInMeters', 'gridLineHeightInMeters'])
    if (!isValid) return

    await updateGridLines(
      {
        gridLineWidthInMeters: toNumberOrNull(form.getValues('gridLineWidthInMeters')),
        gridLineHeightInMeters: toNumberOrNull(form.getValues('gridLineHeightInMeters')),
      },
      {
        onSuccess: () => {
          invalidateProjectCache.getProject(customerId, project.localityId, project.id as EntityId)
          navigate(
            AppRoutesNavigation.Project.project(
              project.customerId as EntityId,
              project.localityId as EntityId,
              project.id as EntityId,
            ),
          )
          pop()
        },
        onError: () => {
          toast.error(translate('unExpectedError', { ns: 'common-api-message' }))
        },
      },
    )
  }

  const pendingChangesPop = (hasChanges: boolean, callBackFn: () => void) => {
    if (hasChanges) {
      showWarningDialog(
        translate('unsavedChangesWarning', { ns: 'position' }),
        translate('unsavedChangesWarningSubtext', { ns: 'position' }),
        false,
        [
          {
            text: translate('cancel', { ns: 'common' }),
            callback: warningPop => {
              warningPop()
            },
            variant: 'outlined',
            sx: {
              backgroundColor: getThemeColor(theme, MUIThemeColors.white),
              '&:hover': { backgroundColor: darken(getThemeColor(theme, MUIThemeColors.white), 0.2) },
            },
          },
          {
            text: translate('discard', { ns: 'common' }),
            callback: warningPop => {
              warningPop()
              callBackFn()
            },
          },
        ],
      )
    } else {
      callBackFn()
    }
  }

  return (
    <>
      {withSteps && steps && <FormStepper steps={steps} />}
      <AppForm form={form}>
        <SetupFacilityForm
          project={project}
          form={form}
          setHasPendingChanges={setHasPendingChanges}
          existingPositions={project.positions}
          isConfigurePositions={isConfigurePositions}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Box display={'flex'} gap={1}>
            <GridActionButton
              border={1}
              sx={{ width: '120px', height: '44px' }}
              onClick={() =>
                pendingChangesPop(hasPendingChanges, () => {
                  onStepBack?.(
                    form.getValues('positions'),
                    form.getValues('type') ?? undefined,
                    form.getValues('noOfCages'),
                    form.getValues('noOfPens'),
                    form.getValues('noOfRows'),
                    form.getValues('gridLineWidthInMeters'),
                    form.getValues('gridLineHeightInMeters'),
                  )
                  pop()
                })
              }
              buttonText={translate('back', { ns: 'common' })}
            />
            {isConfigurePositions && (
              <GridActionButton
                border={1}
                sx={{ width: '120px', height: '44px' }}
                onClick={() => {
                  pendingChangesPop(hasPendingChanges, () => {
                    navigate(
                      AppRoutesNavigation.Project.project(
                        project.customerId as EntityId,
                        project.localityId as EntityId,
                        project.id as EntityId,
                      ),
                    )
                    pop()
                  })
                }}
                buttonText={translate('setup-later')}
              />
            )}
            <MainButton
              key="setup-facility-button"
              variant="contained"
              onClick={() => {
                isConfigurePositions
                  ? pendingChangesPop(hasPendingChanges, handleSubmit)
                  : pendingChangesPop(hasPendingChanges, handleComplete)
              }}
            >
              {translate(isConfigurePositions ? 'setup-facility' : 'complete', { ns: 'common' })}
            </MainButton>
          </Box>
        </Box>
      </AppForm>
    </>
  )
}

export default SetupFacilityDialog
